import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { TextInput, PasswordInput, Button } from '@mantine/core';
import { IconUser, IconMail, IconKey, IconDeviceFloppy } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { User } from '@turizi/shared';
import { useUpdateUser } from '@/hooks/api-hooks';
import { localUser, updateMe } from '@/services/session-service';

export interface UserDetailsTabProps {
    user?: User;
}

export function UserDetailsTab({ user: propUser }: UserDetailsTabProps) {
    const { t } = useTranslation();
    const outletContext = useOutletContext<{ user?: User } | null>();
    const user = propUser || outletContext?.user;

    const currentUser = localUser();
    const updateUserMutation = useUpdateUser();

    const [name, setName] = useState(user?.name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [password, setPassword] = useState('');

    useEffect(() => {
        if (user) {
            setName(user.name || '');
            setEmail(user.email || '');
        }
    }, [user?.name, user?.email]);

    if (!user) {
        return null;
    }

    const isSelf = currentUser && user.id && String(currentUser.id) === String(user.id);

    const handleSave = async () => {
        if (!email.trim()) {
            notifications.show({
                title: t('error'),
                message: t('email_required'),
                color: 'red',
            });
            return;
        }

        const payload: Partial<User> = {
            id: Number(user.id),
            name: name.trim(),
            email: email.trim(),
        };

        if (password.trim()) {
            payload.password = password.trim();
        }

        const { error } = await updateUserMutation.mutateAsync(payload);
        if (error) {
            notifications.show({
                title: t('error'),
                message: typeof error === 'string' ? error : error.message || t('unknown_error'),
                color: 'red',
            });
        } else {
            notifications.show({
                title: t('success'),
                message: t('user_updated_successfully'),
                color: 'green',
            });
            setPassword('');
            if (isSelf) {
                await updateMe(true);
            }
        }
    };

    return (
        <div className="space-y-6">
            <div className="space-y-4">
                <TextInput
                    label={t('name')}
                    placeholder={t('name')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    leftSection={<IconUser size={16} className="text-gray-400" />}
                    radius="md"
                />

                <TextInput
                    label={t('email')}
                    placeholder={t('email')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    type="email"
                    leftSection={<IconMail size={16} className="text-gray-400" />}
                    radius="md"
                />

                <PasswordInput
                    label={t('new_password')}
                    placeholder={t('new_password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    description={t('leave_blank_password_help')}
                    leftSection={<IconKey size={16} className="text-gray-400" />}
                    radius="md"
                />
            </div>

            <div className="flex justify-end">
                <Button
                    color="black"
                    size="sm"
                    radius="md"
                    loading={updateUserMutation.isPending}
                    onClick={handleSave}
                    leftSection={<IconDeviceFloppy size={16} />}
                >
                    {t('save_changes')}
                </Button>
            </div>
        </div>
    );
}

export default UserDetailsTab;
